import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, {
  GameDetailResponse,
  GameLineupPlayerItem,
  GameTimelineEvent,
} from '@/services/communityService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Card, CardContent, CardHeader } from '@/components/shadcn/card';
import { Badge } from '@/components/shadcn/badge';
import { Button as ShadcnButton } from '@/components/shadcn/button';

import { toast } from 'react-hot-toast';
import invasionService from '@/services/invasionService';
import { getTimelineEventLabel } from '@/utils/timelineUtils';
import CatatGolModal from '@/components/community/CatatGolModal';
import CatatKejadianModal from '@/components/community/CatatKejadianModal';
import SubstitutionModal from '@/components/community/SubstitutionModal';
import { historyCanGoBack } from '@/utils/nativeNavigation';
import { hostPaths } from '@/utils/hostPaths';

const normalizeTimeline = (raw: unknown): GameTimelineEvent[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is GameTimelineEvent =>
      e != null && typeof e === 'object' && 'time' in e && 'type' in e && 'team_side' in e
  ) as GameTimelineEvent[];
};

/** Parse time string (e.g. "45", "90+2", "1'") to { minute, added } for sorting. */
const parseTime = (t: string): { minute: number; added: number } => {
  const s = String(t ?? '').trim();
  const plus = s.indexOf('+');
  if (plus === -1) {
    const minute = Number.parseInt(s.replace(/\D/g, ''), 10) || 0;
    return { minute, added: 0 };
  }
  const minute = Number.parseInt(s.slice(0, plus).replace(/\D/g, ''), 10) || 0;
  const added = Number.parseInt(s.slice(plus + 1).replace(/\D/g, ''), 10) || 0;
  return { minute, added };
};

const sortTimelineByTimeDesc = (events: GameTimelineEvent[]): GameTimelineEvent[] => {
  return [...events].sort((a, b) => {
    const ta = parseTime(a.time);
    const tb = parseTime(b.time);
    if (tb.minute !== ta.minute) return tb.minute - ta.minute;
    return tb.added - ta.added;
  });
};

const getScoreFromTimeline = (timeline: GameTimelineEvent[]) => {
  let home = 0;
  let away = 0;
  timeline.forEach((e) => {
    if (e.type === 'goal' || e.type === 'own_goal') {
      if (e.team_side === 'home') home += 1;
      else if (e.team_side === 'away') away += 1;
    }
  });
  return { home, away };
};

const getPlayerStatsFromTimeline = (timeline: GameTimelineEvent[], playerParticipantId: number) => {
  let goals = 0;
  let assists = 0;
  let saves = 0;
  let keyPasses = 0;
  let clearances = 0;
  let freestyles = 0;
  let yellowCards = 0;
  let redCards = 0;
  let foulsBlunders = 0;
  let ownGoals = 0;

  timeline.forEach((e) => {
    if (e.player_id === playerParticipantId) {
      if (e.type === 'goal' && e.detail !== 'own_goal') goals += 1;
      if (e.type === 'own_goal' || (e.type === 'goal' && e.detail === 'own_goal')) ownGoals += 1;
      if (e.type === 'save') saves += 1;
      if (e.type === 'key_pass') keyPasses += 1;
      if (e.type === 'clearance') clearances += 1;
      if (e.type === 'freestyle') freestyles += 1;
      if (e.type === 'yellow_card' || e.detail === 'yellow_card') yellowCards += 1;
      if (e.type === 'red_card' || e.detail === 'red_card') redCards += 1;
      if (
        e.type === 'foul' ||
        e.type === 'yellow_card' ||
        e.type === 'red_card' ||
        e.detail === 'yellow_card' ||
        e.detail === 'red_card'
      ) {
        foulsBlunders += 1;
      }
    }
    if (e.type === 'goal' && e.detail !== 'own_goal' && e.assist_id === playerParticipantId) {
      assists += 1;
    }
  });

  return {
    goals,
    assists,
    saves,
    keyPasses,
    clearances,
    freestyles,
    yellowCards,
    redCards,
    foulsBlunders,
    ownGoals,
  };
};

const formatTimer = (totalSeconds: number): string => {
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const MabarRekapPage = () => {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper to save timer state to localStorage synchronously
  const saveTimerToStorage = (slug: string, seconds: number, isRunning: boolean) => {
    try {
      const data = JSON.stringify({
        timerSeconds: seconds,
        isTimerRunning: isRunning,
        updatedAt: Date.now(),
      });
      localStorage.setItem(`mabar_timer_${slug}`, data);
    } catch {}
  };

  // Helper to read initial timer state from localStorage
  const initialTimer = useMemo(() => {
    if (!gameSlug) return { seconds: 0, isRunning: false };
    try {
      const saved = localStorage.getItem(`mabar_timer_${gameSlug}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        const isRunning = Boolean(parsed.isTimerRunning);
        let seconds = typeof parsed.timerSeconds === 'number' ? parsed.timerSeconds : 0;
        if (isRunning && typeof parsed.updatedAt === 'number') {
          const elapsed = Math.floor((Date.now() - parsed.updatedAt) / 1000);
          seconds = Math.max(0, seconds + elapsed);
        }
        return { seconds, isRunning };
      }
    } catch {}
    return { seconds: 0, isRunning: false };
  }, [gameSlug]);

  const [timerSeconds, setTimerSeconds] = useState<number>(initialTimer.seconds);
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(initialTimer.isRunning);

  const isTimerRunningRef = useRef<boolean>(initialTimer.isRunning);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    isTimerRunningRef.current = isTimerRunning;
  }, [isTimerRunning]);

  const clearIntervalTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  // Timer interval effect
  useEffect(() => {
    clearIntervalTimer();
    if (isTimerRunning) {
      timerIntervalRef.current = setInterval(() => {
        if (!isTimerRunningRef.current) return;
        setTimerSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      clearIntervalTimer();
    };
  }, [isTimerRunning]);

  // Sync timer state changes to storage
  useEffect(() => {
    if (!gameSlug) return;
    saveTimerToStorage(gameSlug, timerSeconds, isTimerRunning);
  }, [gameSlug, timerSeconds, isTimerRunning]);

  const handleToggleTimer = () => {
    const nextIsRunning = !isTimerRunning;
    isTimerRunningRef.current = nextIsRunning;
    if (!nextIsRunning) {
      clearIntervalTimer();
    }
    setIsTimerRunning(nextIsRunning);
    if (gameSlug) {
      saveTimerToStorage(gameSlug, timerSeconds, nextIsRunning);
    }
  };

  const handleStopTimer = () => {
    clearIntervalTimer();
    isTimerRunningRef.current = false;
    setIsTimerRunning(false);
    setTimerSeconds(0);
    if (gameSlug) {
      saveTimerToStorage(gameSlug, 0, false);
    }
    toast.success('Timer pertandingan dihentikan & di-reset ke 00:00');
  };



  // Modal States
  const [goalModal, setGoalModal] = useState<{
    teamSide: 'home' | 'away';
    teamName: string;
    capturedTimeStr: string;
    capturedSeconds: number;
  } | null>(null);

  const [eventModal, setEventModal] = useState<{
    player: GameLineupPlayerItem;
    teamSide: 'home' | 'away';
    capturedTimeStr: string;
    capturedSeconds: number;
  } | null>(null);

  const [substitutionModal, setSubstitutionModal] = useState<{
    playerOut: GameLineupPlayerItem;
    teamSide: 'home' | 'away';
  } | null>(null);

  const [eventToDelete, setEventToDelete] = useState<GameTimelineEvent | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPushingMmr, setIsPushingMmr] = useState(false);
  const [isPushSuccess, setIsPushSuccess] = useState(false);
  const [showPushMmrAlert, setShowPushMmrAlert] = useState(false);

  const isFinalizedGame = Boolean(isPushSuccess || game?.is_finalized);



  const refetchGame = () => {
    if (!gameSlug) return;
    communityService
      .getGameBySlug(gameSlug)
      .then(setGame)
      .catch(() => setGame(null));
  };

  const handlePushMmrClick = () => {
    if (isFinalizedGame) {
      toast.error('Pertandingan ini sudah difinalisasi dan poin MMR telah terhitung.');
      return;
    }
    setShowPushMmrAlert(true);
  };

  const confirmPushMmr = async () => {
    setShowPushMmrAlert(false);
    await handlePushMmrFromRekap();
  };

  const confirmDeleteTimelineEvent = async () => {
    if (!gameSlug || !game || !eventToDelete) return;
    const originalTimeline = normalizeTimeline(game.timeline ?? []);
    const eventIndex = originalTimeline.findIndex(
      (e) =>
        e.time === eventToDelete.time &&
        e.type === eventToDelete.type &&
        e.team_side === eventToDelete.team_side &&
        e.player_id === eventToDelete.player_id
    );
    if (eventIndex === -1) return;

    setIsDeleting(true);
    try {
      const newTimeline = originalTimeline.filter((_, idx) => idx !== eventIndex);
      await communityService.updateGameTimeline(gameSlug, newTimeline);
      refetchGame();
    } catch (err) {
      console.error('Gagal menghapus kejadian:', err);
    } finally {
      setIsDeleting(false);
      setEventToDelete(null);
    }
  };

  useEffect(() => {
    if (!gameSlug) return;
    setLoading(true);
    communityService
      .getGameBySlug(gameSlug)
      .then((data) => {
        setGame(data);
        if (data?.event_slug && gameSlug) {
          sessionStorage.setItem('mabar_event_slug_' + gameSlug, data.event_slug);
          sessionStorage.setItem('last_mabar_event_slug', data.event_slug);
        }
      })
      .catch(() => setGame(null))
      .finally(() => setLoading(false));
  }, [gameSlug]);

  // Broadcast rekap state & listen for push MMR trigger from MobileTopbar
  useEffect(() => {
    const broadcastState = () => {
      window.dispatchEvent(
        new CustomEvent('sporto-rekap-state', {
          detail: { isFinalizedGame, isPushingMmr },
        })
      );
    };

    broadcastState();

    const handleRequestState = () => {
      broadcastState();
    };

    const handleTriggerPush = () => {
      handlePushMmrClick();
    };

    window.addEventListener('sporto-request-rekap-state', handleRequestState);
    window.addEventListener('sporto-trigger-push-mmr', handleTriggerPush);

    return () => {
      window.removeEventListener('sporto-request-rekap-state', handleRequestState);
      window.removeEventListener('sporto-trigger-push-mmr', handleTriggerPush);
    };
  }, [isFinalizedGame, isPushingMmr, handlePushMmrClick]);

  const timeline = useMemo(() => normalizeTimeline(game?.timeline ?? []), [game?.timeline]);

  const timelineWithoutKickOff = useMemo(
    () =>
      sortTimelineByTimeDesc(timeline.filter((e) => (e.type || '').toLowerCase() !== 'kick_off')),
    [timeline]
  );

  const { home: homeScore, away: awayScore } = useMemo(
    () => getScoreFromTimeline(timeline),
    [timeline]
  );

  const homeLineup = useMemo(
    () => (game?.lineups ?? []).filter((l) => l.team_side === 'home'),
    [game?.lineups]
  );
  const awayLineup = useMemo(
    () => (game?.lineups ?? []).filter((l) => l.team_side === 'away'),
    [game?.lineups]
  );

  const homeOnField = homeLineup.filter((l) => l.is_starting);
  const homeBench = homeLineup.filter((l) => !l.is_starting);
  const awayOnField = awayLineup.filter((l) => l.is_starting);
  const awayBench = awayLineup.filter((l) => !l.is_starting);

  const backUrl = game?.event_slug
    ? hostPaths.mabar.analitik(game.event_slug)
    : hostPaths.mabar.list;
  const goBack = () => {
    if (historyCanGoBack()) {
      navigate(-1);
      return;
    }
    navigate(backUrl);
  };

  const homeName = game?.home_team_name || 'Green Team';
  const awayName = game?.away_team_name || 'Navy Team';

  // Helper trigger that snapshots match time and navigates to Select Player page
  const handleNavigateToSelectPlayer = (eventType: string, teamSide?: 'home' | 'away') => {
    if (isFinalizedGame || !gameSlug) return;
    const calcMin = Math.floor(timerSeconds / 60);
    const timeTag = `${calcMin}'`;
    let url = `/host/mabar/game/${gameSlug}/rekap/select-player?event=${eventType}&timeTag=${timeTag}&seconds=${timerSeconds}`;
    if (teamSide) {
      url += `&team=${teamSide}`;
    }
    navigate(url);
  };

  if (loading) {
    return (
      <div className="space-y-4 pb-24">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Game tidak ditemukan</p>
        <ShadcnButton
          variant="outline"
          className="mt-4 rounded-xl"
          onClick={() => navigate('/host/mabar')}
        >
          Kembali
        </ShadcnButton>
      </div>
    );
  }

  const handlePushMmrFromRekap = async () => {
    if (!game || !game.event_slug) return;
    setIsPushingMmr(true);
    try {
      const allPlayers = game.lineups || [];
      const playerStatsArray = allPlayers.map((l) => {
        const stats = getPlayerStatsFromTimeline(timeline, l.player_participant_id);
        const isHome = l.team_side === 'home';
        const oppScore = isHome ? awayScore : homeScore;
        return {
          matchPlayerId: l.id,
          goals: stats.goals,
          assists: stats.assists,
          keyPasses: stats.keyPasses,
          clearances: stats.clearances,
          saves: stats.saves,
          isCleanSheet: oppScore === 0,
          freestyles: stats.freestyles,
          foulsBlunders: stats.foulsBlunders,
          ownGoals: stats.ownGoals,
        };
      });

      await invasionService.finalizeMatch(game.event_slug, game.id, {
        scoreTeamA: homeScore,
        scoreTeamB: awayScore,
        playerStats: playerStatsArray,
      });

      toast.success('⚡ MMR berhasil dihitung & di-push ke pemain!');
      setIsPushSuccess(true);
      refetchGame();
    } catch (err: any) {
      if (err.message?.includes('difinalisasi')) {
        setIsPushSuccess(true);
      }
      toast.error(err.message || 'Gagal memproses Push MMR');
    } finally {
      setIsPushingMmr(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 pt-0 pb-28 space-y-5">
      {/* Top Bar Header */}
      <div className="hidden md:flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={goBack}
          className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900 cursor-pointer"
        >
          <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          <span className="text-base font-bold text-[#0E1726]">Rekap Lapangan</span>
        </button>

        <div className="flex items-center gap-2">

          {isFinalizedGame ? (
            <ShadcnButton
              disabled
              className="bg-emerald-100 border border-emerald-300 text-emerald-800 font-bold text-xs gap-1.5 cursor-not-allowed opacity-80 rounded-full"
            >
              <Icon icon="ph:check-circle-bold" className="w-4 h-4 text-emerald-600" />
              <span>✅ MMR Final</span>
            </ShadcnButton>
          ) : (
            <ShadcnButton
              onClick={handlePushMmrClick}
              disabled={isPushingMmr}
              className="bg-gradient-to-r from-[#FF8552] to-[#FC480D] hover:from-[#FC480D] hover:to-[#e03d07] text-white font-bold text-xs gap-1.5 cursor-pointer rounded-full shadow-xs"
            >
              <Icon icon="ph:lightning-bold" className="w-4 h-4" />
              {isPushingMmr ? 'Memproses...' : 'Push MMR'}
            </ShadcnButton>
          )}
        </div>
      </div>

      {/* Main Score & Team Header Card */}
      <Card className="rounded-3xl border border-gray-200 shadow-2xs overflow-hidden bg-white">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between text-xs text-gray-400 font-semibold mb-3">
            <span>Game 1</span>
            <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-[#FC480D] px-2.5 py-0.5 rounded-full text-xs font-bold font-mono">
              <Icon icon="ph:timer-bold" className="w-3.5 h-3.5" />
              <span>{formatTimer(timerSeconds)}</span>
              <span className="text-[9px] uppercase tracking-wide">
                ({isTimerRunning ? 'Berjalan' : 'Terhenti'})
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 py-2">
            {/* Home Team */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xl flex items-center justify-center mb-1">
                {(homeName || 'G').charAt(0).toUpperCase()}
              </div>
              <span className="text-xs font-bold text-gray-800 text-center truncate max-w-[100px]">
                {homeName}
              </span>
            </div>

            {/* Score */}
            <div className="flex flex-col items-center">
              <div className="text-3xl font-extrabold text-[#0E1726] tracking-tight">
                {homeScore} - {awayScore}
              </div>
              <span className="text-[11px] font-medium text-gray-400 mt-0.5">Skor sementara</span>
            </div>

            {/* Away Team */}
            <div className="flex flex-col items-center flex-1">
              <div className="w-14 h-14 rounded-full bg-slate-100 text-slate-800 font-bold text-xl flex items-center justify-center mb-1">
                {(awayName || 'N').charAt(0).toUpperCase()}
              </div>
              <span className="text-xs font-bold text-gray-800 text-center truncate max-w-[100px]">
                {awayName}
              </span>
            </div>
          </div>

          {/* Big Goal Action Buttons */}
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              disabled={isFinalizedGame}
              onClick={() => handleNavigateToSelectPlayer('goal', 'home')}
              className={`h-11 rounded-2xl bg-[#22C55E] hover:bg-[#16a34a] text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer ${
                isFinalizedGame ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
              }`}
            >
              <Icon icon="ph:soccer-ball-bold" className="w-4 h-4" />
              <span>Catat Gol</span>
            </button>

            <button
              type="button"
              disabled={isFinalizedGame}
              onClick={() => handleNavigateToSelectPlayer('goal', 'away')}
              className={`h-11 rounded-2xl bg-[#1E293B] hover:bg-[#0f172a] text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer ${
                isFinalizedGame ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
              }`}
            >
              <Icon icon="ph:soccer-ball-bold" className="w-4 h-4" />
              <span>Catat Gol</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Catat Kejadian Section (New List Card from Images 1 & 2) */}
      <Card className="rounded-3xl border border-gray-200 shadow-2xs overflow-hidden bg-white">
        <CardHeader className="pb-3 pt-4 px-4 sm:px-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-orange-50 flex items-center justify-center text-[#FC480D]">
              <Icon icon="ph:sliders-horizontal-bold" className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-extrabold text-[#0E1726] text-sm">Catat Kejadian</h3>
              <p className="text-xs text-gray-400 font-medium">
                Pilih kejadian dulu, lalu tentukan pemainnya.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-4 divide-y divide-gray-100">
          {[
            {
              type: 'save',
              label: 'Penyelamatan (Save)',
              description: 'Kiper menggagalkan peluang gol',
              icon: 'ph:shield-check-bold',
              iconBg: 'bg-emerald-100 text-emerald-600',
            },
            {
              type: 'clearance',
              label: 'Clearance',
              description: 'Bola dibuang dari area berbahaya',
              icon: 'ph:shield-bold',
              iconBg: 'bg-blue-100 text-blue-600',
            },
            {
              type: 'key_pass',
              label: 'Key Pass',
              description: 'Umpan yang menciptakan peluang',
              icon: 'ph:arrow-elbow-down-right-bold',
              iconBg: 'bg-purple-100 text-purple-600',
            },
            {
              type: 'freestyle',
              label: 'Freestyle',
              description: 'Aksi individu yang menghibur',
              icon: 'ph:sparkle-bold',
              iconBg: 'bg-amber-100 text-amber-600',
            },
            {
              type: 'foul',
              label: 'Pelanggaran (Foul)',
              description: 'Terjadi pelanggaran ringan',
              icon: 'ph:warning-bold',
              iconBg: 'bg-amber-100 text-amber-600',
            },
            {
              type: 'yellow_card',
              label: 'Kartu Kuning',
              description: 'Peringatan resmi dari wasit',
              icon: 'ph:rectangle-bold',
              iconBg: 'bg-amber-100 text-amber-500',
            },
            {
              type: 'red_card',
              label: 'Kartu Merah',
              description: 'Pemain harus meninggalkan lapangan',
              icon: 'ph:rectangle-bold',
              iconBg: 'bg-red-100 text-red-500',
            },
            {
              type: 'substitute',
              label: 'Pergantian Pemain',
              description: 'Pemain masuk menggantikan',
              icon: 'ph:arrows-left-right-bold',
              iconBg: 'bg-blue-100 text-blue-600',
            },
            {
              type: 'own_goal',
              label: 'Gol Bunuh Diri (Own Goal)',
              description: 'Pemain lawan mencetak gol ke gawang sendiri',
              icon: 'ph:arrows-clockwise-bold',
              iconBg: 'bg-red-100 text-red-600',
            },
          ].map((opt) => (
            <div
              key={opt.type}
              onClick={() => handleNavigateToSelectPlayer(opt.type)}
              className="p-3 hover:bg-gray-50 flex items-center justify-between gap-3 transition-colors cursor-pointer rounded-2xl"
            >
              <div className="flex items-center gap-3.5 min-w-0">
                <div
                  className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${opt.iconBg}`}
                >
                  <Icon icon={opt.icon} className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-xs sm:text-sm font-bold text-[#0E1726] truncate">
                    {opt.label}
                  </h4>
                  <p className="text-[11px] text-gray-400 font-medium truncate">
                    {opt.description}
                  </p>
                </div>
              </div>
              <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 shrink-0" />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Riwayat Pertandingan Card */}
      <Card className="rounded-3xl border border-gray-200 shadow-2xs overflow-hidden bg-white">
        <CardHeader className="pb-3 pt-4 px-4 sm:px-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-orange-50 flex items-center justify-center text-[#FC480D]">
                <Icon icon="ph:clock-counter-clockwise-bold" className="w-4 h-4" />
              </div>
              <h3 className="font-bold text-[#0E1726] text-sm">Riwayat Pertandingan</h3>
            </div>
            <Badge className="bg-emerald-50 text-emerald-600 border border-emerald-200 text-xs font-bold px-2.5 py-0.5 rounded-full">
              {timelineWithoutKickOff.length} Kejadian
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-3">
          <div className="space-y-3">
            {timelineWithoutKickOff.map((evt, idx) => (
              <TimelineEventRow
                key={idx}
                event={evt}
                game={game}
                onDelete={() => setEventToDelete(evt)}
                isDeleting={isDeleting}
              />
            ))}
            {timelineWithoutKickOff.length === 0 && (
              <div className="text-center py-6 text-xs font-semibold text-gray-400">
                Belum ada kejadian pertandingan tercatat
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 🛑 STICKY BOTTOM MATCH TIMER BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-2.5 sm:py-3 shadow-xl">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
          <div className="flex flex-col">
            <span className="text-xl sm:text-2xl font-black text-[#0E1726] tracking-tight font-mono">
              {formatTimer(timerSeconds)}
            </span>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider ${
                isTimerRunning ? 'text-emerald-600' : 'text-amber-600'
              }`}
            >
              {isTimerRunning ? 'Berjalan' : 'Terhenti'}
            </span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2.5">
            {/* Pause / Resume Button */}
            <button
              type="button"
              onClick={handleToggleTimer}
              className="h-8 sm:h-10 px-2.5 sm:px-4 rounded-full border-2 border-[#FC480D] text-[#FC480D] text-xs font-bold flex items-center gap-1 hover:bg-orange-50 transition-all cursor-pointer active:scale-95"
            >
              <Icon
                icon={isTimerRunning ? 'ph:pause-bold' : 'ph:play-bold'}
                className="w-3.5 h-3.5 sm:w-4 sm:h-4"
              />
              <span>{isTimerRunning ? 'Pause' : 'Mulai'}</span>
            </button>

            {/* Stop Button */}
            <button
              type="button"
              onClick={handleStopTimer}
              className="h-8 sm:h-10 px-2.5 sm:px-4 rounded-full border-2 border-red-500 text-red-500 text-xs font-bold flex items-center gap-1 hover:bg-red-50 transition-all cursor-pointer active:scale-95"
            >
              <Icon icon="ph:square-bold" className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current" />
              <span>Stop</span>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Bottom Sheet Modal */}
      {!!eventToDelete && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200 !mt-0"
          onClick={() => !isDeleting && setEventToDelete(null)}
        >
          <div
            className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-5 shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle indicator */}
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />

            {/* Header Title & Close Button */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-extrabold text-red-600 flex items-center gap-2">
                  <Icon icon="ph:trash-bold" className="w-5 h-5" />
                  <span>Hapus Kejadian?</span>
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Apakah kamu yakin ingin menghapus kejadian ini dari riwayat pertandingan? Tindakan ini tidak dapat dibatalkan.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !isDeleting && setEventToDelete(null)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 cursor-pointer"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            </div>

            {/* Footer Buttons */}
            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setEventToDelete(null)}
                disabled={isDeleting}
                className="h-11 px-4 rounded-full border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={confirmDeleteTimelineEvent}
                disabled={isDeleting}
                className="h-11 px-4 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-bold shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 active:scale-95"
              >
                {isDeleting ? (
                  <>
                    <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                    <span>Menghapus...</span>
                  </>
                ) : (
                  <span>Hapus</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push MMR Confirmation Bottom Sheet Modal */}
      {showPushMmrAlert && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200 !mt-0"
          onClick={() => !isPushingMmr && setShowPushMmrAlert(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-5 shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle indicator */}
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />

            {/* Header Title & Close Button */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-extrabold text-[#FC480D] flex items-center gap-2">
                  <Icon icon="ph:lightning-bold" className="w-5 h-5" />
                  <span>Finalisasi & Push MMR Game</span>
                </h2>
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                  Apakah Anda yakin ingin memproses Push MMR untuk game ini? <br />
                  <strong className="text-gray-700 font-semibold block pt-1">
                    Setelah difinalisasi, data hasil pertandingan ini menjadi final dan poin MMR tidak dapat diubah lagi.
                  </strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => !isPushingMmr && setShowPushMmrAlert(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 cursor-pointer"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            </div>

            {/* Footer Buttons */}
            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowPushMmrAlert(false)}
                disabled={isPushingMmr}
                className="h-11 px-4 rounded-full border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={confirmPushMmr}
                disabled={isPushingMmr}
                className="h-11 px-4 rounded-full bg-[#FC480D] hover:bg-[#e03d07] text-white text-sm font-bold shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 active:scale-95"
              >
                {isPushingMmr ? (
                  <>
                    <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                    <span>Memproses...</span>
                  </>
                ) : (
                  <span>Ya, Push MMR</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {gameSlug && goalModal && (
        <CatatGolModal
          isOpen={!!goalModal}
          onClose={() => setGoalModal(null)}
          gameSlug={gameSlug}
          teamSide={goalModal.teamSide}
          teamName={goalModal.teamName}
          onFieldPlayers={goalModal.teamSide === 'home' ? homeOnField : awayOnField}
          currentTimeline={timeline}
          capturedTimeStr={goalModal.capturedTimeStr}
          capturedSeconds={goalModal.capturedSeconds}
          onSuccess={refetchGame}
        />
      )}

      {gameSlug && eventModal && (
        <CatatKejadianModal
          isOpen={!!eventModal}
          onClose={() => setEventModal(null)}
          gameSlug={gameSlug}
          player={eventModal.player}
          teamSide={eventModal.teamSide}
          currentTimeline={timeline}
          capturedTimeStr={eventModal.capturedTimeStr}
          capturedSeconds={eventModal.capturedSeconds}
          onSuccess={refetchGame}
        />
      )}

      {gameSlug && substitutionModal && (
        <SubstitutionModal
          isOpen={!!substitutionModal}
          onClose={() => setSubstitutionModal(null)}
          gameSlug={gameSlug}
          playerOut={substitutionModal.playerOut}
          teamSide={substitutionModal.teamSide}
          bench={substitutionModal.teamSide === 'home' ? homeBench : awayBench}
          onSuccess={refetchGame}
        />
      )}
    </div>
  );
};





function getTimelineEventIcon(event: GameTimelineEvent) {
  switch (event.type) {
    case 'own_goal':
      return (
        <div className="w-8 h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
          <Icon icon="ph:soccer-ball-bold" className="w-4 h-4" />
        </div>
      );
    case 'goal':
      return (
        <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
          <Icon icon="ph:soccer-ball-bold" className="w-4 h-4" />
        </div>
      );
    case 'key_pass':
      return (
        <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
          <Icon icon="ph:arrow-elbow-down-right-bold" className="w-4 h-4" />
        </div>
      );
    case 'clearance':
      return (
        <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
          <Icon icon="ph:shield-bold" className="w-4 h-4" />
        </div>
      );
    case 'freestyle':
      return (
        <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
          <Icon icon="ph:sparkle-bold" className="w-4 h-4" />
        </div>
      );
    case 'save':
      return (
        <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
          <Icon icon="ph:shield-check-bold" className="w-4 h-4" />
        </div>
      );
    case 'foul':
      return (
        <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
          <Icon icon="ph:warning-bold" className="w-4 h-4" />
        </div>
      );
    case 'yellow_card':
      return (
        <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
          <Icon icon="ph:rectangle-bold" className="w-4 h-4" />
        </div>
      );
    case 'red_card':
      return (
        <div className="w-8 h-8 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
          <Icon icon="ph:rectangle-bold" className="w-4 h-4" />
        </div>
      );
    case 'substitution':
      return (
        <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
          <Icon icon="ph:arrows-left-right-bold" className="w-4 h-4" />
        </div>
      );
    default:
      return (
        <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center shrink-0">
          <Icon icon="ph:info-bold" className="w-4 h-4" />
        </div>
      );
  }
}

function TimelineEventRow({
  event,
  game,
  onDelete,
  isDeleting,
}: {
  event: GameTimelineEvent;
  game: GameDetailResponse;
  onDelete?: () => void;
  isDeleting?: boolean;
}) {
  const isHome = event.team_side === 'home';
  const resolveName = (id: number) =>
    game.lineups.find((l) => l.player_participant_id === id || l.id === id)?.name;

  const teamLabel = isHome ? game.home_team_name || 'Green Team' : game.away_team_name || 'Navy Team';

  let playerName = 'Pemain';
  let typeLabel = event.type;

  if (event.type === 'substitution') {
    const nameOut =
      event.player_id_out != null
        ? (resolveName(event.player_id_out) ?? `#${event.player_id_out}`)
        : '–';
    const nameIn =
      event.player_id_in != null
        ? (resolveName(event.player_id_in) ?? `#${event.player_id_in}`)
        : '–';
    playerName = 'Pergantian Pemain';
    typeLabel = `${nameOut} keluar, ${nameIn} masuk`;
  } else {
    if (event.player_id != null) {
      playerName = resolveName(event.player_id) ?? `#${event.player_id}`;
    }
    const assistName = event.assist_id != null ? resolveName(event.assist_id) : null;
    typeLabel = getTimelineEventLabel(event, assistName, {
      saveLabel: 'Penyelamatan (Save)',
      foulLabel: 'Pelanggaran (Foul)',
    });
  }

  const isOwnGoal = event.type === 'own_goal' || event.detail === 'own_goal';

  return (
    <div className="flex items-center justify-between p-3 rounded-2xl border border-gray-100 bg-white">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xs font-mono font-bold text-gray-400 w-6 shrink-0">{event.time}</span>
        {getTimelineEventIcon(event)}
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#0E1726] truncate">{playerName}</p>
          <p className="text-xs text-gray-400 font-medium truncate">
            {typeLabel} · {isOwnGoal ? `Gol untuk ${teamLabel}` : teamLabel}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting}
        className="w-8 h-8 rounded-full text-gray-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-all cursor-pointer shrink-0"
        title="Hapus kejadian"
      >
        <Icon icon="ph:trash-bold" className="w-4 h-4" />
      </button>
    </div>
  );
}

export default MabarRekapPage;
